import { forwardRef, cloneElement, isValidElement } from 'react';
import { Pagination as AntdPagination } from 'antd';
import {
    AltArrowLeftLinearIcon,
    AltArrowRightLinearIcon,
    DoubleAltArrowLeftLinearIcon,
    DoubleAltArrowRightLinearIcon,
} from '@solar-icons/react';
import clsx from 'clsx';
import styles from './Pagination.module.css';

const PREV_ICON = (
    <button type="button" className={clsx('ant-pagination-item-link', styles.vecPaginationArrow)} aria-label="Previous page">
        <AltArrowLeftLinearIcon size={16} />
    </button>
);
const NEXT_ICON = (
    <button type="button" className={clsx('ant-pagination-item-link', styles.vecPaginationArrow)} aria-label="Next page">
        <AltArrowRightLinearIcon size={16} />
    </button>
);
const JUMP_PREV_ICON = (
    <button type="button" className="ant-pagination-item-link" aria-label="Jump back 5 pages">
        <div className="ant-pagination-item-container">
            <DoubleAltArrowLeftLinearIcon size={16} className="ant-pagination-item-link-icon" />
            <span className="ant-pagination-item-ellipsis">&bull;&bull;&bull;</span>
        </div>
    </button>
);
const JUMP_NEXT_ICON = (
    <button type="button" className="ant-pagination-item-link" aria-label="Jump forward 5 pages">
        <div className="ant-pagination-item-container">
            <DoubleAltArrowRightLinearIcon size={16} className="ant-pagination-item-link-icon" />
            <span className="ant-pagination-item-ellipsis">&bull;&bull;&bull;</span>
        </div>
    </button>
);

/**
 * @param {Object} props - Same shape as antd's own `Pagination`, forwarded as-is.
 * @param {boolean} [props.sticky=false] - Pins the pagination frame to the bottom of its scroll container.
 *
 * Fixes accessibility gaps in antd's default: wraps the list in a `<nav>` landmark, gives every
 * icon-only control (prev/next/jump-5) an accessible name instead of relying on the `title`
 * attribute (unreliable for keyboard/touch/AT users), and marks the active page with
 * `aria-current="page"` (antd never sets this).
 */
const Pagination = forwardRef(({ className, current, itemRender, prevIcon, nextIcon, jumpPrevIcon, jumpNextIcon, showTitle, sticky = false, ...props }, ref) => {
    const withCurrentPage = (page, type, originalElement) => {
        const element = itemRender ? itemRender(page, type, originalElement) : originalElement;
        if (type === 'page' && page === current && isValidElement(element)) {
            return cloneElement(element, { 'aria-current': 'page' });
        }
        return element;
    };

    return (
        <div className={clsx(styles.vecPaginationFrame, sticky && styles.vecPaginationSticky)}>
            <nav aria-label="Pagination Navigation">
                <AntdPagination
                    ref={ref}
                    current={current}
                    prevIcon={prevIcon ?? PREV_ICON}
                    nextIcon={nextIcon ?? NEXT_ICON}
                    jumpPrevIcon={jumpPrevIcon ?? JUMP_PREV_ICON}
                    jumpNextIcon={jumpNextIcon ?? JUMP_NEXT_ICON}
                    showTitle={showTitle ?? false}
                    itemRender={withCurrentPage}
                    className={clsx(styles.vecPagination, className)}
                    {...props}
                />
            </nav>
        </div>
    );
});
Pagination.displayName = 'Pagination';

export default Pagination;
